import { Fragment, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  IconPlus,
  IconTrash,
  IconChevronRight,
  IconGripVertical,
} from "@tabler/icons-react";
import { toast } from "sonner";
import {
  createOnDropHandler,
  dragAndDropFeature,
  hotkeysCoreFeature,
  keyboardDragAndDropFeature,
  renamingFeature,
  selectionFeature,
  syncDataLoaderFeature,
} from "@headless-tree/core";
import { useTree } from "@headless-tree/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { SectionHeader } from "@/components/ui/section-header";
import { EmptyState } from "@/components/ui/empty-state";
import {
  usePhotoCategories,
  useReorderCategories,
  photoCategoryKeys,
} from "@/api/hooks/use-photo-categories";
import type { PhotoCategory } from "@/api/hooks/use-photo-categories";
import { asPhotoCategoryId } from "@/types/identifiers";
import { api, throwIfError } from "@/api/client";

// --- Flat tree data types ---

type CategoryItem = {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  allow_multiple: boolean;
  parent_id: string | null;
  children: string[];
};


function flattenCategories(categories: PhotoCategory[]): Record<string, CategoryItem> {
  const data: Record<string, CategoryItem> = {
    root: {
      id: "root",
      name: "Root",
      description: null,
      sort_order: 0,
      allow_multiple: false,
      parent_id: null,
      children: categories.map((c) => c.id),
    },
  };

  function walk(cat: PhotoCategory) {
    data[cat.id] = {
      id: cat.id,
      name: cat.name,
      description: cat.description,
      sort_order: cat.sort_order,
      allow_multiple: cat.allow_multiple,
      parent_id: cat.parent_id,
      children: cat.children.map((c) => c.id),
    };
    cat.children.forEach(walk);
  }

  categories.forEach(walk);
  return data;
}

// --- Mutations ---

function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      parent_id?: string | null;
      name: string;
      description?: string | null;
      sort_order: number;
      allow_multiple: boolean;
    }) => {
      const result = await api.POST("/api/photo-categories", { body });
      return throwIfError(result, "Failed to create category");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: photoCategoryKeys.all });
      toast.success("Category created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      parent_id?: string | null;
      name?: string;
      description?: string | null;
      sort_order?: number;
      allow_multiple?: boolean;
    }) => {
      const result = await api.PUT("/api/photo-categories/{category_id}", {
        params: { path: { category_id: id } },
        body,
      });
      return throwIfError(result, "Failed to update category");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: photoCategoryKeys.all });
      toast.success("Category updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await api.DELETE("/api/photo-categories/{category_id}", {
        params: { path: { category_id: id } },
      });
      return throwIfError(result, "Failed to delete category");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: photoCategoryKeys.all });
      toast.success("Category deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// --- Detail Panel ---

function DetailPanel({
  item,
  onClose,
}: {
  item: CategoryItem;
  onClose: () => void;
}) {
  const isLeaf = item.children.length === 0;
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description ?? "");
  const [allowMultiple, setAllowMultiple] = useState(item.allow_multiple);
  const updateMut = useUpdateCategory();

  const handleSave = () => {
    updateMut.mutate(
      {
        id: item.id,
        name,
        description: description || null,
        allow_multiple: allowMultiple,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="border rounded-lg p-4 space-y-4 w-72 shrink-0">
      <SectionHeader className="mb-0">Category Details</SectionHeader>
      <div className="space-y-3">
        <div>
          <Label htmlFor="detail-name">Name</Label>
          <Input
            id="detail-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="detail-desc">Description</Label>
          <Input
            id="detail-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="detail-multi"
            checked={allowMultiple}
            onCheckedChange={setAllowMultiple}
            disabled={!isLeaf}
          />
          <Label htmlFor="detail-multi" className={!isLeaf ? "text-muted-foreground" : ""}>
            Allow multiple photos
          </Label>
          {!isLeaf && (
            <span className="text-xs text-muted-foreground">(leaf only)</span>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={updateMut.isPending}
        >
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

// --- Category Tree (only rendered when data is available) ---

function CategoryTree({ categories }: { categories: PhotoCategory[] }) {
  const reorderMut = useReorderCategories();
  const createMut = useCreateCategory();
  const deleteMut = useDeleteCategory();
  const updateMut = useUpdateCategory();

  const [treeState, setTreeState] = useState<Record<string, unknown>>({});
  const [flatData, setFlatData] = useState(() => flattenCategories(categories));

  // Sync from server when query data changes (refetch after mutation, etc.)
  useEffect(() => {
    setFlatData(flattenCategories(categories));
  }, [categories]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDrop = createOnDropHandler((item: any, newChildren: string[]) => {
    const parentId = item.getId();

    // Optimistic local update — React batches these when called from the same handler
    setFlatData((prev) => {
      const next = { ...prev };
      if (next[parentId]) {
        next[parentId] = { ...next[parentId], children: newChildren };
        newChildren.forEach((childId, idx) => {
          if (next[childId]) {
            next[childId] = {
              ...next[childId],
              parent_id: parentId === "root" ? null : parentId,
              sort_order: idx,
            };
          }
        });
      }
      return next;
    });

    // Persist only the affected children
    const reorderItems = newChildren.map((childId, idx) => ({
      id: asPhotoCategoryId(childId),
      parent_id: parentId === "root" ? null : asPhotoCategoryId(parentId),
      sort_order: idx,
    }));
    if (reorderItems.length > 0) {
      reorderMut.mutate(reorderItems);
    }
  });

  const tree = useTree<CategoryItem>({
    state: treeState,
    setState: setTreeState,
    rootItemId: "root",
    getItemName: (item) => item.getItemData().name,
    isItemFolder: () => true,
    canReorder: true,
    indent: 20,
    dataLoader: {
      getItem: (id) => flatData[id] ?? { id, name: id, description: null, sort_order: 0, allow_multiple: false, parent_id: null, children: [] },
      getChildren: (id) => flatData[id]?.children ?? [],
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onDrop: handleDrop as any,
    onRename: (item, value) => {
      updateMut.mutate({ id: item.getId(), name: value });
    },
    features: [
      syncDataLoaderFeature,
      selectionFeature,
      hotkeysCoreFeature,
      dragAndDropFeature,
      keyboardDragAndDropFeature,
      renamingFeature,
    ],
  });

  const items = tree.getItems();
  const selectedId = items.find((i) => i.isSelected() && i.getId() !== "root")?.getId() ?? null;
  const selectedItem = selectedId ? flatData[selectedId] : null;

  const handleAddChild = (parentId: string | null) => {
    const name = prompt("Category name:");
    if (!name?.trim()) return;
    createMut.mutate({
      parent_id: parentId,
      name: name.trim(),
      sort_order: 0,
      allow_multiple: false,
    });
  };

  const handleDelete = (id: string) => {
    if (confirm("Delete this category?")) {
      deleteMut.mutate(id);
    }
  };

  return (
    <>
      <PageHeader
        title="Photo Categories"
        action={
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAddChild(null)}
          >
            <IconPlus className="mr-1.5 h-4 w-4" />
            New Root
          </Button>
        }
      />

      <div className="flex gap-6 mt-6">
        <div className="border rounded-lg flex-1 min-w-0">
          <div {...tree.getContainerProps()} className="py-1">
            {items.map((item) => (
              <Fragment key={item.getId()}>
                {item.isRenaming() ? (
                  <div
                    className="flex items-center px-3 py-1.5"
                    style={{
                      paddingLeft: `${item.getItemMeta().level * 20 + 12}px`,
                    }}
                  >
                    <input
                      {...item.getRenameInputProps()}
                      className="flex-1 text-sm border rounded px-2 py-1 outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                ) : (
                  <button
                    {...item.getProps()}
                    className={`flex items-center gap-2 w-full text-left py-1.5 px-3 group ${
                      item.isSelected()
                        ? "bg-accent"
                        : "hover:bg-muted/50"
                    } ${item.isDragTarget() ? "bg-blue-50 dark:bg-blue-950" : ""}`}
                    style={{
                      paddingLeft: `${item.getItemMeta().level * 20 + 12}px`,
                    }}
                  >
                    <IconGripVertical className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0 cursor-grab" />

                    <IconChevronRight
                      className={`h-4 w-4 shrink-0 transition-transform ${
                        item.isExpanded() ? "rotate-90" : ""
                      } ${item.getItemData().children.length === 0 ? "invisible" : ""}`}
                    />

                    <span className="flex-1 text-sm font-medium truncate">
                      {item.getItemName()}
                    </span>

                    {item.getItemData().allow_multiple && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded shrink-0">
                        multi
                      </span>
                    )}

                    <span className="text-xs text-muted-foreground shrink-0">
                      #{item.getItemData().sort_order}
                    </span>

                    <div className="opacity-0 group-hover:opacity-100 flex gap-1 shrink-0">
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddChild(item.getId());
                        }}
                        className="p-1 text-muted-foreground hover:text-foreground cursor-pointer"
                      >
                        <IconPlus className="h-3.5 w-3.5" />
                      </span>
                      {item.getItemData().children.length === 0 && (
                        <span
                          role="button"
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.getId());
                          }}
                          className="p-1 text-muted-foreground hover:text-destructive cursor-pointer"
                        >
                          <IconTrash className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>
                  </button>
                )}
              </Fragment>
            ))}
            {items.length === 0 && (
              <EmptyState message="No categories." className="py-8" />
            )}
            <div style={tree.getDragLineStyle()} className="h-0.5 bg-primary absolute pointer-events-none" />
          </div>
        </div>

        {selectedItem && (
          <DetailPanel
            key={selectedItem.id}
            item={selectedItem}
            onClose={() => setTreeState((s) => ({ ...s, selectedItems: [] }))}
          />
        )}
      </div>
    </>
  );
}

// --- Main Page ---

export function AdminPhotoCategoriesPage() {
  const { data: categories, isLoading } = usePhotoCategories();

  if (isLoading || !categories) {
    return (
      <div>
        <Skeleton className="h-8 w-48 rounded-lg" />
        <div className="mt-6 space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return <CategoryTree categories={categories as PhotoCategory[]} />;
}
